
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdVisibility,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdSearch

} from 'react-icons/md';
import './Employees.css';
import '../pages/Dashboard.css';
import Swal from 'sweetalert2';
import { GET_ALL_EMPLOYEES_API, DELETE_EMPLOYEE_API } from '../config/API';
import axios from 'axios';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { useUser } from '../context/UserContext';




const Employees = () => {
    const navigate = useNavigate();
    const { hasPermission } = useUser();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const fetchEmployees = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_EMPLOYEES_API,
                {
                    params: {
                        page: currentPage,
                        limit: limit,
                        search: debouncedSearch || undefined
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    },

                });


            if (response.data.status) {
                setEmployees(response.data.data || []);
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || response.data.totalPages || 1);
                setTotalRecords(pagination.total || pagination.totalRecords || response.data.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearch, limit]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees, currentPage, debouncedSearch, limit]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };


    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);






    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);


    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Associate?',
            text: 'Are you sure you want to delete this associate?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-delete-btn',
                cancelButton: 'swal-cancel-btn',
            },

        });
        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_EMPLOYEE_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Associate has been deleted.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    
                    // Optimistic update
                    setEmployees(prev => prev.filter(employee => (employee.id || employee._id) !== id));
                    
                    // If it was the last item on the page and not on page 1, go to previous page
                    if (employees.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        fetchEmployees();
                    }
                } else {
                    Swal.fire('Error', response.data.error || response.data.message || 'Failed to delete associate', 'error');
                }
            } catch (error) {
                console.error('Error deleting associate:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'An error occurred while deleting', 'error');
            }
        }
    }

    return (
        <div className="dashboard">
            <div className="employees-header">
                <h2 className="section-title" style={{ margin: 0 }}>Associates</h2>
                {hasPermission('EMPLOYEES', 'create') && (
                    <Link to="/employees/new" className="add-employee-btn">
                        <MdAdd size={20} />
                        Add Associate
                    </Link>
                )}
            </div>

            <div className="card-container-employees">
                {/* Search Bar */}
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <div style={{ position: 'relative', maxWidth: '500px' }}>
                        <MdSearch
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '1.25rem'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search associates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.625rem 1rem 0.625rem 2.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                background: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#e5e7eb',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: '#4b5563',
                                    fontSize: '12px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                {loading ? (
                    <Loader text="Fetching associates..." />
                ) :
                    employees.length === 0 ? (
                        <NoData text="No associates found. Click 'Add Associate' to create one." />
                    ) : (
                        <>
                            <div className="table-container">
                                <table >
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Contact Number</th>
                                            <th>Email ID</th>
                                            <th>Username</th>
                                            <th>Gender</th>
                                            <th>Role</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employees.map((employee, index) => (
                                            <tr key={employee.id || employee._id}>
                                                <td>
                                                    <strong>{employee.firstName} {employee.lastName}</strong>
                                                </td>
                                                <td>{employee.contactNumber || '-'}</td>
                                                <td>{employee.email || '-'}</td>
                                                <td>{employee.userName || '-'}</td>
                                                <td>{employee.gender || '-'}</td>
                                                <td>
                                                    <span className={`role-badge ${employee.roleName === 'Admin' ? 'role-admin' : 'role-employee'}`}>
                                                        {employee.roleName || 'No Role'}
                                                    </span>
                                                </td>
                                                <td className={`action-cell ${openMenuId === (employee.id || employee._id) ? 'menu-open' : ''}`}>
                                                    <button
                                                        className="action-menu-btn"
                                                        onClick={(e) => toggleMenu(employee.id || employee._id, e)}
                                                        title="Actions"
                                                    >
                                                        <MdMoreVert size={20} />
                                                    </button>

                                                    <ActionMenuPortal
                                                        isOpen={openMenuId === (employee.id || employee._id)}
                                                        anchorRect={anchorRect}
                                                        onClose={() => setOpenMenuId(null)}
                                                    >
                                                        <div className="action-dropdown">
                                                            {/* <button className="action-item" onClick={() => console.log('View', employee.id)}>
                                                                <MdVisibility size={16} /> View Details
                                                            </button> */}
                                                            {hasPermission('EMPLOYEES', 'update') && (
                                                                <button className="action-item" onClick={() => navigate(`/employees/edit/${employee.id || employee._id}`)}>
                                                                    <MdEdit size={16} /> Edit Associate
                                                                </button>
                                                            )}
                                                            {hasPermission('EMPLOYEES', 'delete') && (
                                                                <button className="action-item delete" onClick={() => handleDelete(employee.id || employee._id)}>
                                                                    <MdDelete size={16} /> Delete
                                                                </button>
                                                            )}
                                                        </div>
                                                    </ActionMenuPortal>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="mobile-card-view" >
                                {employees.map((employee) => (
                                    <div key={employee.id || employee._id} className="employee-card">
                                        <div className="card-header">
                                            <div className="card-title">
                                                <strong>{employee.firstName} {employee.lastName}</strong>
                                                <span className={`role-badge ${employee.Role?.roleName === 'Admin' ? 'role-admin' : 'role-employee'}`}>
                                                    {employee.Role?.roleName || 'No Role'}
                                                </span>
                                            </div>
                                            <button
                                                className="action-menu-btn"
                                                onClick={(e) => toggleMenu(employee.id, e)}
                                                title="Actions"
                                            >
                                                <MdMoreVert size={20} />
                                            </button>
                                            <ActionMenuPortal
                                                isOpen={openMenuId === (employee.id || employee._id)}
                                                anchorRect={anchorRect}
                                                onClose={() => setOpenMenuId(null)}
                                            >
                                                <div className="action-dropdown shadow-lg">
                                                    {/* <button className="action-item" onClick={() => console.log('View', employee.id)}>
                                                        <MdVisibility size={16} /> View Details
                                                    </button> */}
                                                    {hasPermission('EMPLOYEES', 'update') && (
                                                        <button className="action-item" onClick={() => navigate(`/employees/edit/${employee.id || employee._id}`)}>
                                                            <MdEdit size={16} /> Edit Associate
                                                        </button>
                                                    )}
                                                    {hasPermission('EMPLOYEES', 'delete') && (
                                                        <button
                                                            className="action-item delete"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(employee.id || employee._id);
                                                            }}
                                                        >
                                                            <MdDelete size={16} /> Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </ActionMenuPortal>
                                        </div>
                                        <div className="card-body">
                                            <div className="card-row">
                                                <span className="card-label">Contact:</span>
                                                <span className="card-value">{employee.contactNumber}</span>
                                            </div>
                                            <div className="card-row">
                                                <span className="card-label">Email:</span>
                                                <span className="card-value">{employee.email}</span>
                                            </div>
                                            <div className="card-row">
                                                <span className="card-label">Username:</span>
                                                <span className="card-value">{employee.userName}</span>
                                            </div>
                                            <div className="card-row">
                                                <span className="card-label">Gender:</span>
                                                <span className="card-value">{employee.gender}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                totalRecords={totalRecords}
                                currentCount={employees.length}
                                entityName="Associates"
                             limit={limit} onLimitChange={handleLimitChange} />
                        </>
                    )}
            </div>
        </div>
    );
};

export default Employees;
